/**
 * materialList.js: pure aggregator for the quote Material List (IDI-105).
 *
 * Ruled layout (IDI-105, Adrian 2026-09):
 *   Items | Product | Square Footage | R-Value | Item Qty | Qty on Hand
 *   -- Ceilings ------------------------------  (AREA band)
 *    Accessible - SPF | QS118 ...   | 1,500 | 3"   | 1.13  | -0.70
 *    Accessible - SPF | HANDI-CLEANER|     0 |      | 1.00  |
 *
 *   - Grouped by AREA (band); SUB-AREA goes in the Items column.
 *   - NO MONEY. This is a materials order in coverage units, not the
 *     internal cost report (that is QuoteAnalysisReport).
 *   - Qty on Hand is the catalog's post-draw stock and is NEVER clamped;
 *     a negative value is the "order this" signal.
 *   - An accessory with no square footage shows 0 and still carries a qty.
 *   - Sum by (sub-area, product, unit), never across units (IDI-40).
 *
 * Item Qty source, first hit wins:
 *   1. line.bidItemUsage (stamped at build/re-price, IDI-173/364)
 *   2. line.coverageSqftPerUnit + coverageUnit via unitsFor
 *   3. non-sqft lines (each / linear ft): the line quantity itself
 *   4. otherwise null (no coverage known -> "—", never a guess)
 *
 * Alternates are not on the list (they are options, not the scope).
 * Pure-labor custom lines (no material cost, no sq ft, no accessories) are
 * skipped, the same rule woMaterialsProjection uses.
 */
import { unitsFor } from './productCoverage';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round(n * 100) / 100;

const isSqftUnit = (u) => {
  const s = String(u || 'sq ft').toLowerCase().replace(/[\s_.-]/g, '');
  return s === 'sqft' || s === 'sf' || s === 'squarefeet' || s === 'squarefoot';
};

/** "3in" / "3.5in" -> 3" / 3.5"; R-values pass through. */
export function displayRValue(line) {
  const choice = line?.coverageChoice;
  if (typeof choice === 'string' && /^\d+(\.\d+)?in$/.test(choice)) {
    return `${choice.slice(0, -2)}"`;
  }
  const r = line?.rValue;
  if (r === null || r === undefined || r === '') return '';
  const s = String(r);
  return /^\d+(\.\d+)?$/.test(s) ? `R-${s}` : s;
}

/** Units this line draws, or null when coverage is unknown. */
export function lineItemQty(line) {
  if (line?.bidItemUsage !== null && line?.bidItemUsage !== undefined && line.bidItemUsage !== '') {
    const n = Number(line.bidItemUsage);
    if (Number.isFinite(n)) return n;
  }
  const sqft = num(line?.sqft);
  if (num(line?.coverageSqftPerUnit) > 0 && sqft > 0) {
    return unitsFor(sqft, num(line.coverageSqftPerUnit), line.coverageUnit);
  }
  if (!isSqftUnit(line?.unit) && sqft > 0) return sqft;
  if (line?.isCustom && num(line?.quantity) > 0) return num(line.quantity);
  return null;
}

function onHandFrom(map, key) {
  if (!map || key === null || key === undefined) return null;
  const row = map.get(String(key));
  if (!row) return null;
  const v = row.qtyOnHand ?? row.qty_onhand;
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function indexCatalog(rows) {
  const m = new Map();
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r) return;
    [r.id, r.sku, r.partNumber].forEach((k) => {
      if (k !== null && k !== undefined && k !== '' && !m.has(String(k))) m.set(String(k), r);
    });
  });
  return m;
}

/**
 * @param {Array} lineItems  committed quote line items
 * @param {{catalog?: Array, accessoryCatalog?: Array}} opts  rows carrying qtyOnHand
 * @returns {Array<{areaLabel: string, rows: Array}>}  area bands in first-seen order.
 *   row = { item, product, unit, sqft, rValue, qty, qtyOnHand, kind }
 */
export function buildMaterialList(lineItems, { catalog, accessoryCatalog } = {}) {
  if (!Array.isArray(lineItems)) return [];
  const products = indexCatalog(catalog);
  const accessories = indexCatalog(accessoryCatalog);
  const bands = new Map();

  const add = (areaLabel, key, seed, sqft, qty) => {
    if (!bands.has(areaLabel)) bands.set(areaLabel, { areaLabel, rows: new Map() });
    const rows = bands.get(areaLabel).rows;
    if (!rows.has(key)) rows.set(key, { ...seed, sqft: 0, qty: null });
    const row = rows.get(key);
    row.sqft += sqft;
    if (qty !== null) row.qty = (row.qty ?? 0) + qty;
  };

  for (const line of lineItems) {
    if (!line || typeof line !== 'object') continue;
    const areaLabel = line.areaLabel || line.area_label || 'Uncategorized';
    const item = line.subAreaLabel || line.sub_area_label || '';
    const accs = Array.isArray(line.accessories) ? line.accessories : [];
    const sqft = num(line.sqft);
    const unit = line.unit || 'sq ft';
    const isLaborOnly = num(line.materialCost) <= 0 && sqft <= 0 && accs.length === 0;

    if (!isLaborOnly && (sqft > 0 || num(line.materialCost) > 0)) {
      const product = line.productLabel || line.categoryLabel || line.name || 'Material';
      const rValue = displayRValue(line);
      const productKey = line.productId || product;
      add(
        areaLabel,
        ['p', item, productKey, unit, rValue].join('\u0000'),
        {
          kind: 'product',
          item,
          product,
          unit,
          rValue,
          qtyOnHand: onHandFrom(products, line.productId),
        },
        isSqftUnit(unit) ? sqft : 0,
        lineItemQty(line),
      );
    }

    for (const a of accs) {
      if (!a || typeof a !== 'object') continue;
      const accKey = a.accessory_id || a.id || a.name;
      add(
        areaLabel,
        ['a', item, accKey, a.unit || 'ea'].join('\u0000'),
        {
          kind: 'accessory',
          item,
          product: a.name || 'Accessory',
          unit: a.unit || 'ea',
          rValue: '',
          qtyOnHand: onHandFrom(accessories, a.accessory_id),
        },
        0,
        num(a.qty),
      );
    }
  }

  return [...bands.values()]
    .map((b) => ({
      areaLabel: b.areaLabel,
      rows: [...b.rows.values()].map((r) => ({
        ...r,
        sqft: round2(r.sqft),
        qty: r.qty === null ? null : round2(r.qty),
      })),
    }))
    .filter((b) => b.rows.length > 0);
}

/** Flat rows for the PDF / email payload (area carried on each row). */
export function flattenMaterialList(bands) {
  return (bands || []).flatMap((b) => b.rows.map((r) => ({
    area: b.areaLabel,
    item: r.item,
    product: r.product,
    unit: r.unit,
    sqft: r.sqft,
    rValue: r.rValue,
    qty: r.qty,
    qtyOnHand: r.qtyOnHand,
  })));
}
